import type {
  AssignPathwayResponse,
  AttemptResponse,
  AuditEventsResponse,
  BillingAccountResponse,
  BillingSummaryResponse,
  CancelOrderRequest,
  CatalogResponse,
  ConfirmOrderRequest,
  CompleteResponse,
  CreateLocationRequest,
  CreateLocationResponse,
  CreateOrganizationResponse,
  CreateSetupLinkResponse,
  EnrollResponse,
  EndEntitlementResponse,
  GrantEntitlementResponse,
  InviteResponse,
  InvitationReferencesResponse,
  KnowledgeCheckPublic,
  LearnerDetailResponse,
  LearnersResponse,
  LocationSupplies,
  MediaUrlResponse,
  MeResponse,
  MyLearningResponse,
  MyRecordsResponse,
  OrderResponse,
  OrdersResponse,
  OrganizationMembersResponse,
  OrganizationsResponse,
  PlaceOrderRequest,
  PracticeRosterResponse,
  ProgramFeeStartResponse,
  ProgramFeeStopResponse,
  ProgressResponse,
  RepMeResponse,
  RepStatementsResponse,
  RepVolumeResponse,
  SuppliesResponse,
  ShipOrderRequest,
  StaffOrderResponse,
  StaffOrdersResponse,
  SupplyOrderStatus,
  TestNotificationResponse,
  UpdateMembershipRequest,
  UpdateMembershipResponse,
  UpdateSupplySettingsRequest,
  UpsertBillingAccountRequest,
  UpsertBillingAccountResponse,
} from "@allergy123/contracts";
import type {
  AntigenDetail,
  AntigensResponse,
  CatalogItemDetail,
  CatalogItemsResponse,
  CheckResponse,
  CreateDraftResponse,
  CreateFamilyRequest,
  CreateMixRecipeRequest,
  EntryInput,
  FamiliesResponse,
  FamilySummary,
  LocationCatalog,
  MixRecipe,
  MixRecipesResponse,
  OrganizationLocationsResponse,
  PanelCompareResponse,
  PanelVersionDetail,
  PositionInput,
  ProfileCompareResponse,
  ProfileVersionDetail,
  RegionDefault,
  RegionDefaultsResponse,
  ResolveRegionDefaultResponse,
  SetLocationCatalogRequest,
  UpdateAntigenRequest,
  UpdateCatalogItemRequest,
  UpsertRegionDefaultRequest,
} from "@allergy123/contracts/allergens";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    readonly field?: string,
    /** The whole JSON error body, for the codes that carry more than `field` (for example `issues` or `profiles`). */
    readonly details: Readonly<Record<string, unknown>> = {},
  ) {
    super(`${status} ${code}${field ? ` (${field})` : ""}`);
  }
}

export interface ApiClientConfig {
  /** Base URL including the version segment, e.g. `https://api.allergy123.com/v1` or `http://localhost:8787/v1`. */
  baseUrl: string;
  /**
   * Returns the bearer token or null when signed out. For the platform API this is the Cognito **ID token**:
   * an API Gateway REST Cognito authorizer without scopes validates ID tokens, and the claims the API reads
   * (email, cognito:groups) are only in it.
   */
  token: () => Promise<string | null>;
  /** The organization to act under when the caller belongs to several; verified server-side. */
  organizationId?: () => string | undefined;
  fetch?: typeof fetch;
}

export interface CallOptions {
  idempotencyKey?: string;
  headers?: Record<string, string>;
  /** Abort after this long; default 30 seconds. */
  timeoutMs?: number;
}

/** The transport every client shares: bearer token, organization header, JSON bodies, `ApiError` on failure. */
export function createTransport(config: ApiClientConfig) {
  const doFetch = config.fetch ?? ((input, init) => fetch(input, init));
  // The API Gateway stage URL output ends with a slash; paths here start with one.
  const baseUrl = config.baseUrl.replace(/\/+$/, "");

  return async function call<T>(method: string, path: string, body?: unknown, options: CallOptions | string = {}): Promise<T> {
    const opts = typeof options === "string" ? { idempotencyKey: options } : options;
    const token = await config.token();
    const headers: Record<string, string> = { accept: "application/json", ...(opts.headers ?? {}) };
    if (token) headers.authorization = `Bearer ${token}`;
    if (body !== undefined) headers["content-type"] = "application/json";
    if (opts.idempotencyKey) headers["idempotency-key"] = opts.idempotencyKey;
    const org = config.organizationId?.();
    if (org) headers["x-organization-id"] = org;
    // A request that never settles would leave a screen waiting forever; the API answers in well under this.
    const signal = typeof AbortSignal !== "undefined" && "timeout" in AbortSignal ? AbortSignal.timeout(opts.timeoutMs ?? 30_000) : undefined;
    const res = await doFetch(`${baseUrl}${path}`, { method, headers, body: body === undefined ? undefined : JSON.stringify(body), cache: "no-store", ...(signal ? { signal } : {}) });
    if (!res.ok) {
      let code = "request_failed";
      let field: string | undefined;
      let details: Record<string, unknown> = {};
      try {
        const err = (await res.json()) as { error?: string; field?: string } | null;
        if (err && typeof err === "object" && !Array.isArray(err)) {
          code = err.error ?? code;
          field = err.field;
          details = err;
        }
      } catch {
        /* non-JSON error body */
      }
      throw new ApiError(res.status, code, field, details);
    }
    return (await res.json()) as T;
  };
}

/** One method per route. Every call sends the bearer token; the server decides what the caller may see. */
export function createApiClient(config: ApiClientConfig) {
  const call = createTransport(config);

  return {
    me: () => call<MeResponse>("GET", "/me"),
    catalog: () => call<CatalogResponse>("GET", "/catalog"),
    check: (code: string) => call<KnowledgeCheckPublic>("GET", `/catalog/checks/${encodeURIComponent(code)}`),
    learning: {
      me: () => call<MyLearningResponse>("GET", "/learning/me"),
      records: () => call<MyRecordsResponse>("GET", "/learning/records"),
      enroll: (moduleCode: string, organizationId?: string) => call<EnrollResponse>("POST", "/learning/enrollments", { moduleCode, organizationId }),
      progress: (classCode: string, positionSeconds: number) => call<ProgressResponse>("PUT", `/learning/classes/${encodeURIComponent(classCode)}/progress`, { positionSeconds }),
      complete: (classCode: string) => call<CompleteResponse>("POST", `/learning/classes/${encodeURIComponent(classCode)}/complete`),
      attempt: (checkCode: string, answers: Record<string, string>, submissionKey: string) => call<AttemptResponse>("POST", `/learning/checks/${encodeURIComponent(checkCode)}/attempts`, { answers }, submissionKey),
    },
    media: (classCode: string, kind: MediaUrlResponse["kind"]) => call<MediaUrlResponse>("GET", `/media/classes/${encodeURIComponent(classCode)}/${kind}`),
    practice: {
      roster: (organizationId: string) => call<PracticeRosterResponse>("GET", `/practice/${organizationId}/roster`),
      assign: (organizationId: string, userId: string, pathwayId: string) => call<AssignPathwayResponse>("POST", `/practice/${organizationId}/assignments`, { userId, pathwayId }),
    },
    /**
     * The practice's billing view (ADR 0008), under the organization in scope (`organizationId` in the config) for a
     * member holding `clinical:manage`. `place` has no idempotency ledger: show the result, never retry it blindly; a
     * duplicate can be cancelled while `received`.
     */
    billing: {
      summary: () => call<BillingSummaryResponse>("GET", "/billing/summary"),
      /** Test-kit balances and reorder settings per location the caller may see (`clinical:read`). */
      supplies: {
        list: () => call<SuppliesResponse>("GET", "/billing/supplies"),
        update: (locationId: string, input: UpdateSupplySettingsRequest) => call<LocationSupplies>("PUT", `/billing/supplies/${encodeURIComponent(locationId)}`, input),
      },
      orders: {
        list: (locationId?: string) => call<OrdersResponse>("GET", `/billing/orders${locationId ? `?locationId=${encodeURIComponent(locationId)}` : ""}`),
        place: (input: PlaceOrderRequest) => call<OrderResponse>("POST", "/billing/orders", input),
        cancel: (orderId: string) => call<OrderResponse>("POST", `/billing/orders/${orderId}/cancel`),
      },
    },
    admin: {
      invitationReferences: () => call<InvitationReferencesResponse>("GET", "/admin/invitation-references"),
      organizations: () => call<OrganizationsResponse>("GET", "/admin/organizations"),
      createOrganization: (name: string, slug?: string) => call<CreateOrganizationResponse>("POST", "/admin/organizations", { name, slug }),
      invite: (input: { email: string; givenName?: string; familyName?: string; organizationId?: string; role?: "owner" | "admin" | "member"; products?: ("learn" | "rep-training" | "clinical")[]; pathwayId?: string }) =>
        call<InviteResponse>("POST", "/admin/invitations", input),
      grant: (input: { product: "learn" | "rep-training" | "clinical"; subjectType: "user" | "organization"; subjectId: string; endsAt?: string }) => call<GrantEntitlementResponse>("POST", "/admin/entitlements", input),
      endEntitlement: (entitlementId: string) => call<EndEntitlementResponse>("POST", `/admin/entitlements/${entitlementId}/end`),
      members: (organizationId: string) => call<OrganizationMembersResponse>("GET", `/admin/organizations/${organizationId}/members`),
      updateMember: (organizationId: string, userId: string, input: UpdateMembershipRequest) => call<UpdateMembershipResponse>("PUT", `/admin/organizations/${organizationId}/members/${userId}`, input),
      /** A new location with its name and zone (C5); its tray panel and profile are set with `catalog.setLocationCatalog`. */
      createLocation: (organizationId: string, input: CreateLocationRequest) => call<CreateLocationResponse>("POST", `/admin/organizations/${organizationId}/locations`, input),
      learners: () => call<LearnersResponse>("GET", "/admin/learners"),
      learner: (id: string) => call<LearnerDetailResponse>("GET", `/admin/learners/${id}`),
      audit: () => call<AuditEventsResponse>("GET", "/admin/audit"),
      /** Queues a generic test notification to the caller's own address; the server picks the recipient, so there is no body. */
      sendTestNotification: () => call<TestNotificationResponse>("POST", "/admin/notifications/test"),
      /** An organization's billing account (ADR 0008): plan, Stripe customer, payment method, Program Fee subscription. */
      billing: {
        account: (organizationId: string) => call<BillingAccountResponse>("GET", `/admin/organizations/${organizationId}/billing`),
        upsertAccount: (organizationId: string, input: UpsertBillingAccountRequest) => call<UpsertBillingAccountResponse>("PUT", `/admin/organizations/${organizationId}/billing`, input),
        /** A Stripe-hosted page where the practice saves a card or bank account; staff pass the link on. */
        createSetupLink: (organizationId: string) => call<CreateSetupLinkResponse>("POST", `/admin/organizations/${organizationId}/billing/setup-link`),
        startProgramFee: (organizationId: string) => call<ProgramFeeStartResponse>("POST", `/admin/organizations/${organizationId}/billing/program-fee/start`),
        stopProgramFee: (organizationId: string) => call<ProgramFeeStopResponse>("POST", `/admin/organizations/${organizationId}/billing/program-fee/stop`),
        /** Supply orders across organizations: confirm charges the saved payment method through one Stripe invoice; every mutation is version-gated. */
        orders: {
          list: (filter: { status?: SupplyOrderStatus; organizationId?: string } = {}) => {
            const query = Object.entries(filter).filter(([, v]) => v).map(([k, v]) => `${k}=${encodeURIComponent(v!)}`).join("&");
            return call<StaffOrdersResponse>("GET", `/admin/billing/orders${query ? `?${query}` : ""}`);
          },
          get: (orderId: string) => call<StaffOrderResponse>("GET", `/admin/billing/orders/${orderId}`),
          confirm: (orderId: string, input: ConfirmOrderRequest) => call<StaffOrderResponse>("POST", `/admin/billing/orders/${orderId}/confirm`, input),
          ship: (orderId: string, input: ShipOrderRequest) => call<StaffOrderResponse>("POST", `/admin/billing/orders/${orderId}/ship`, input),
          cancel: (orderId: string, input: CancelOrderRequest) => call<StaffOrderResponse>("POST", `/admin/billing/orders/${orderId}/cancel`, input),
        },
      },
      catalog: {
        antigens: () => call<AntigensResponse>("GET", "/admin/catalog/antigens"),
        antigen: (id: string) => call<AntigenDetail>("GET", `/admin/catalog/antigens/${id}`),
        updateAntigen: (id: string, patch: UpdateAntigenRequest) => call<AntigenDetail>("PUT", `/admin/catalog/antigens/${id}`, patch),
        linkProduct: (antigenId: string, catalogItemId: string, equivalenceNote?: string) => call<AntigenDetail>("POST", `/admin/catalog/antigens/${antigenId}/products`, { catalogItemId, equivalenceNote }),
        unlinkProduct: (antigenId: string, catalogItemId: string) => call<AntigenDetail>("DELETE", `/admin/catalog/antigens/${antigenId}/products/${catalogItemId}`),
        items: () => call<CatalogItemsResponse>("GET", "/admin/catalog/catalog-items"),
        item: (id: string) => call<CatalogItemDetail>("GET", `/admin/catalog/catalog-items/${id}`),
        updateItem: (id: string, patch: UpdateCatalogItemRequest) => call<CatalogItemDetail>("PUT", `/admin/catalog/catalog-items/${id}`, patch),
        recipes: () => call<MixRecipesResponse>("GET", "/admin/catalog/mix-recipes"),
        createRecipe: (input: CreateMixRecipeRequest) => call<MixRecipe>("POST", "/admin/catalog/mix-recipes", input),
        panels: () => call<FamiliesResponse>("GET", "/admin/catalog/tray-panels"),
        createPanel: (input: CreateFamilyRequest) => call<FamilySummary>("POST", "/admin/catalog/tray-panels", input),
        panelVersion: (id: string) => call<PanelVersionDetail>("GET", `/admin/catalog/tray-panels/versions/${id}`),
        createPanelDraft: (panelId: string, fromVersionId: string | null) => call<CreateDraftResponse>("POST", `/admin/catalog/tray-panels/${panelId}/drafts`, { fromVersionId }),
        savePanelPositions: (versionId: string, positions: PositionInput[]) => call<PanelVersionDetail>("PUT", `/admin/catalog/tray-panels/versions/${versionId}/positions`, { positions }),
        checkPanel: (versionId: string) => call<CheckResponse>("GET", `/admin/catalog/tray-panels/versions/${versionId}/check`),
        publishPanel: (versionId: string, note?: string) => call<PanelVersionDetail>("POST", `/admin/catalog/tray-panels/versions/${versionId}/publish`, { note }),
        comparePanels: (a: string, b: string) => call<PanelCompareResponse>("GET", `/admin/catalog/tray-panels/compare?a=${encodeURIComponent(a)}&b=${encodeURIComponent(b)}`),
        profiles: () => call<FamiliesResponse>("GET", "/admin/catalog/allergen-profiles"),
        createProfile: (input: CreateFamilyRequest) => call<FamilySummary>("POST", "/admin/catalog/allergen-profiles", input),
        profileVersion: (id: string) => call<ProfileVersionDetail>("GET", `/admin/catalog/allergen-profiles/versions/${id}`),
        createProfileDraft: (profileId: string, fromVersionId: string | null) => call<CreateDraftResponse>("POST", `/admin/catalog/allergen-profiles/${profileId}/drafts`, { fromVersionId }),
        saveProfileEntries: (versionId: string, entries: EntryInput[]) => call<ProfileVersionDetail>("PUT", `/admin/catalog/allergen-profiles/versions/${versionId}/entries`, { entries }),
        checkProfile: (versionId: string) => call<CheckResponse>("GET", `/admin/catalog/allergen-profiles/versions/${versionId}/check`),
        publishProfile: (versionId: string, note?: string) => call<ProfileVersionDetail>("POST", `/admin/catalog/allergen-profiles/versions/${versionId}/publish`, { note }),
        compareProfiles: (a: string, b: string) => call<ProfileCompareResponse>("GET", `/admin/catalog/allergen-profiles/compare?a=${encodeURIComponent(a)}&b=${encodeURIComponent(b)}`),
        regionDefaults: () => call<RegionDefaultsResponse>("GET", "/admin/catalog/region-defaults"),
        upsertRegionDefault: (input: UpsertRegionDefaultRequest) => call<RegionDefault>("PUT", "/admin/catalog/region-defaults", input),
        deleteRegionDefault: (id: string) => call<{ deleted: true }>("DELETE", `/admin/catalog/region-defaults/${id}`),
        resolveRegionDefault: (state: string, zip?: string) => call<ResolveRegionDefaultResponse>("GET", `/admin/catalog/region-defaults/resolve?state=${encodeURIComponent(state)}${zip ? `&zip=${encodeURIComponent(zip)}` : ""}`),
        organizationLocations: (organizationId: string) => call<OrganizationLocationsResponse>("GET", `/admin/catalog/organizations/${organizationId}/locations`),
        setLocationCatalog: (locationId: string, input: SetLocationCatalogRequest) => call<LocationCatalog>("PUT", `/admin/catalog/locations/${locationId}`, input),
      },
    },
    reps: {
      me: () => call<RepMeResponse>("GET", "/reps/me"),
      volume: () => call<RepVolumeResponse>("GET", "/reps/volume"),
      statements: () => call<RepStatementsResponse>("GET", "/reps/statements"),
    },
  };
}

export type ApiClient = ReturnType<typeof createApiClient>;
